/**
 * Firebase Email Link Authentication Module
 *
 * This module provides a custom implementation of Firebase's passwordless email link authentication.
 * It replaces the FirebaseUI library with a lightweight custom solution that provides the same
 * functionality while being more flexible and maintainable.
 *
 * Features:
 * - Send passwordless sign-in links via email
 * - Complete sign-in from email link clicks
 * - Manage UI state (loading, success, error)
 * - Handle user sign-out
 * - Emit custom events for integration with other code
 *
 * @requires firebase (global Firebase SDK instance)
 */

import { initializeFirebase, createPageLoaderManager, getFirebaseGlobal, hasFirebaseAuth } from './utils.js';

(function() {
  'use strict';

  // Create page loader manager for this module
  // This allows us to show/hide the loader without relying on window.pageLoader
  const pageLoader = createPageLoaderManager();

  /**
   * Firebase Email Link Auth Class
   * Encapsulates all authentication logic and UI state management
   */
  class FirebaseEmailLinkAuth {
    /**
     * Initialize the auth module
     * @param {string} containerId - The ID of the container element
     * @param {firebase.auth.Auth} auth - Firebase Auth instance
     */
    constructor(containerId, auth) {
      // Store references to Firebase auth and container
      this.containerId = containerId;
      this.auth = auth;

      // Get container element using native DOM API
      const containerElement = document.getElementById(containerId);
      if (containerElement) {
        // console.error('FirebaseEmailLinkAuth: Container element not found:', containerId);
        // return;

        this.container = containerElement;

        // Cache DOM element references for better performance
        // This avoids repeated DOM queries
        this.form = this.container.querySelector('.firebase-auth-form');
        this.emailInput = this.container.querySelector('.firebase-auth-email-input');
        this.submitBtn = this.container.querySelector('.firebase-auth-submit-btn');
        this.spinner = this.container.querySelector('.firebase-auth-btn-spinner');
        this.errorMessage = this.container.querySelector('.firebase-auth-error-message');
        this.errorText = this.container.querySelector('.firebase-auth-error-text');
        this.successMessage = this.container.querySelector('.firebase-auth-success-message');
        this.signedInContainer = this.container.querySelector('.firebase-signed-in-container');
        this.signedInEmail = this.container.querySelector('.firebase-signed-in-email');
        this.signoutBtn = this.container.querySelector('.firebase-signout-btn');

        // Get configuration from data attributes
        // Use dataset API to access data attributes (data-redirect-url becomes redirectUrl)
        const redirectUrlFromData = this.container.dataset.redirectUrl || this.container.getAttribute('data-redirect-url');
        // Convert relative URLs to absolute URLs (Firebase requires absolute URLs)
        if (redirectUrlFromData && redirectUrlFromData.startsWith('/')) {
          this.redirectUrl = window.location.origin + redirectUrlFromData;
        } else {
          this.redirectUrl = redirectUrlFromData || window.location.href;
        }

      }

      // LocalStorage key for persisting the email during the auth flow
      // Firebase requires this to complete sign-in from the email link
      this.emailStorageKey = 'firebaseEmailForSignIn';

      // User data management properties
      // Cache for the current Firebase user object
      this.currentUser = null;
      // Cache for user data fetched from the API
      this.currentUserData = null;
      // Flag to prevent duplicate API calls when fetching user data
      this.userDataFetchInProgress = false;

      // Flag to indicate if user data has been loaded
      this.hasLoadedUserData = false;

      // Bind event handlers to maintain proper 'this' context
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleSignOut = this.handleSignOut.bind(this);

      // Initialize the module
      this.init();
    }

    /**
     * Initialize event listeners and check for email link sign-in
     * This is the main entry point after construction
     */
    init() {
      // Attach form submit handler using native addEventListener
      if (this.form) {
        this.form.addEventListener('submit', this.handleFormSubmit);
      }

      // Attach sign-out button handler using native addEventListener
      if (this.signoutBtn) {
        this.signoutBtn.addEventListener('click', this.handleSignOut);
      }

      // Check if the current URL is an email sign-in link
      // This happens when user clicks the link from their email
      if (this.auth.isSignInWithEmailLink(window.location.href)) {
        this.completeSignIn();
      }

      // Listen for auth state changes
      // This will fire when sign-in completes or when user signs out
      this.auth.onAuthStateChanged((user) => {
        this.handleAuthStateChange(user);
      });
    }

    /**
     * Fetch a fresh REST API nonce from our backend.
     * Used instead of wp_localize_script so the nonce is not stale on cached pages.
     *
     * @returns {Promise<string>} The nonce string
     * @throws {Error} If the fetch fails or response is invalid
     */
    async getRestNonce() {
      const response = await fetch('/wp-json/showlist/v1/nonce', {
        credentials: 'same-origin', // Ensure cookies are sent so nonce matches logged-in WP user if any
      });
      if (!response.ok) {
        throw new Error('Failed to fetch security token. Please try again.');
      }
      const data = await response.json();
      if (!data.nonce) {
        throw new Error('Invalid security token response. Please try again.');
      }
      return data.nonce;
    }

    /**
     * Handle form submission - send sign-in link to email via our backend
     * @param {Event} e - Form submit event
     */
    async handleFormSubmit(e) {
      e.preventDefault();

      // Get and validate email address using native value property
      const email = this.emailInput ? this.emailInput.value.trim() : '';
      if (!email) {
        this.showError('Please enter a valid email address.');
        return;
      }

      // Validate email format with regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.showError('Please enter a valid email address.');
        return;
      }

      // Show loading state
      this.setLoadingState(true);
      this.hideError();

      try {
        // Fetch a fresh nonce from our endpoint (avoids stale nonce on cached pages)
        const nonce = await this.getRestNonce();

        // Derive redirect path from redirectUrl for the backend (e.g. /submit, /account)
        const redirectPath = this.redirectUrl ? new URL(this.redirectUrl, window.location.origin).pathname : window.location.pathname || '/';

        const loginResponse = await fetch('/wp-json/showlist/v1/firebase-users/login', {
          method: 'POST',
          credentials: 'same-origin', // Ensure cookies are sent for nonce verification when WP user is logged in
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
          },
          body: JSON.stringify({
            email: email,
            redirect_path: redirectPath,
          }),
        });

        if (!loginResponse.ok) {
          const errorData = await loginResponse.json().catch(() => ({}));
          const message = errorData.message || 'Failed to send sign-in link. Please try again.';
          throw new Error(message);
        }

        // Store email in localStorage for completing sign-in later
        // Firebase requires this to verify the sign-in is legitimate when user clicks the link
        window.localStorage.setItem(this.emailStorageKey, email);

        // Show success message and hide form
        this.showSuccess();
        if (this.form) {
          this.form.classList.add('hidden');
        }

        // Emit custom event for other code to listen to
        // This allows show-submit.js or other modules to react to successful email send
        this.emitEvent('emailLinkSent', { email });

      } catch (error) {
        // Handle errors and show user-friendly messages
        console.error('Error sending sign-in link:', error);
        this.showError(this.getErrorMessage(error));
      } finally {
        // Always reset loading state
        this.setLoadingState(false);
      }
    }

    /**
     * Complete the sign-in process when user clicks email link
     * This method is called automatically when the page loads with a sign-in link
     */
    async completeSignIn() {
      // Retrieve the email from localStorage
      // This was stored when the user initially requested the sign-in link
      let email = window.localStorage.getItem(this.emailStorageKey);

      // If email is not in localStorage, prompt the user to enter it
      // This can happen if user clicks link from a different device/browser
      if (!email) {
        email = window.prompt('Please provide your email address for confirmation');
        if (!email) {
          this.showError('Email address is required to complete sign-in.');
          return;
        }
      }

      // Show loading state during sign-in
      this.setLoadingState(true);
      this.hideError();

      try {
        // Complete the sign-in using the email and the current URL
        // The URL contains the authentication code from Firebase
        const result = await this.auth.signInWithEmailLink(email, window.location.href);

        // Clear the email from localStorage as it's no longer needed
        window.localStorage.removeItem(this.emailStorageKey);

        // Clean up the URL by removing the auth query parameters
        // This prevents the sign-in from running again if user refreshes
        window.history.replaceState({}, document.title, window.location.pathname);

        // Emit custom event with the signed-in user
        // Other modules can listen for this to update UI or fetch user data
        this.emitEvent('signInComplete', { user: result.user });

      } catch (error) {
        // Handle sign-in errors
        console.error('Error completing sign-in:', error);
        this.showError(this.getErrorMessage(error));

        // Clear stored email on error so user can try again
        window.localStorage.removeItem(this.emailStorageKey);
      } finally {
        this.setLoadingState(false);
      }
    }

    /**
     * Handle authentication state changes
     * @param {firebase.User|null} user - The authenticated user or null if signed out
     */
    handleAuthStateChange(user) {
      if (user) {
        // Store reference to the current user on the class instance
        // This makes the user available throughout the class without passing parameters
        this.currentUser = user;

        // User is signed in - show signed-in display and hide form
        if (this.signedInEmail) {
          this.signedInEmail.textContent = user.email;
        }
        if (this.signedInContainer) {
          this.signedInContainer.classList.add('show');
        }
        if (this.form) {
          this.form.classList.add('hidden');
        }
        this.hideError();
        this.hideSuccess();

        // Fetch user data from API and emit event for page-specific code to consume
        // This centralizes user data fetching in one place
        this.fetchUserData();
      } else {
        // User is signed out - show form and hide signed-in display
        if (this.signedInContainer) {
          this.signedInContainer.classList.remove('show');
        }
        if (this.form) {
          this.form.classList.remove('hidden');
        }
        if (this.emailInput) {
          this.emailInput.value = '';
        }

        // Clear cached user reference and user data, then emit event
        // This notifies page-specific code that user has signed out
        this.currentUser = null;
        this.currentUserData = null;
        this.hasLoadedUserData = true;
        this.emitEvent('userDataCleared');
      }
    }

    /**
     * Fetch user data from the backend API
     * This method gets the user's full profile including credits and other data
     * Uses the stored currentUser reference instead of requiring a parameter
     */
    async fetchUserData() {
      // Prevent duplicate fetches if already in progress
      // This avoids race conditions when auth state changes rapidly
      if (this.userDataFetchInProgress) {
        return;
      }

      // Ensure we have a current user before attempting to fetch data
      // This prevents errors if fetchUserData is called when no user is signed in
      if (!this.currentUser) {
        console.warn('fetchUserData called but no current user available');
        return;
      }

      this.userDataFetchInProgress = true;

      try {
        // Get Firebase ID token for authentication
        // This token is used to authenticate the API request
        // Use the stored currentUser reference instead of a parameter
        const accessToken = await this.currentUser.getIdToken();

        // Fetch user data from the WordPress REST API endpoint
        // This endpoint returns the full user profile including credits
        const response = await fetch('/wp-json/showlist/v1/firebase-users/me', {
          headers: {
            'Authorization': 'Bearer ' + accessToken
          }
        });

        // Check if the API request was successful
        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        // Parse and cache the user data
        this.currentUserData = await response.json();

        // Emit event with full user data for pages to consume
        // Page-specific code can listen for this event and update their UI
        // Use the stored currentUser reference instead of a parameter
        this.emitEvent('userDataLoaded', {
          user: this.currentUser,
          userData: this.currentUserData
        });

      } catch (error) {
        // Log error and emit error event
        console.error('Error fetching user data:', error);
        this.emitEvent('userDataError', { error });
      } finally {
        // Always reset the in-progress flag
        this.userDataFetchInProgress = false;
        this.hasLoadedUserData = true;
      }
    }

    /**
     * Get the cached user data
     * @returns {Object|null} The cached user data or null if not loaded
     */
    getUserData() {
      return this.currentUserData;
    }

    /**
     * Handle sign-out button click
     */
    async handleSignOut() {
      try {
        // Show page loader while signing out
        // This provides visual feedback that something is happening
        pageLoader.show();

        // Sign out from Firebase
        await this.auth.signOut();

        // Emit custom event for other modules
        this.emitEvent('signOutComplete');

        // Reload the page to reset all state
        // This ensures a clean slate for the next user
        window.location.reload();

      } catch (error) {
        console.error('Error signing out:', error);
        this.showError('Failed to sign out. Please try again.');

        // Hide page loader on error
        pageLoader.hide();
      }
    }

    /**
     * Set loading state for the form
     * @param {boolean} isLoading - Whether form is in loading state
     */
    setLoadingState(isLoading) {
      if (isLoading) {
        // Disable form elements and show spinner
        if (this.submitBtn) {
          this.submitBtn.disabled = true;
        }
        if (this.emailInput) {
          this.emailInput.disabled = true;
        }
        if (this.spinner) {
          this.spinner.classList.add('show');
        }
      } else {
        // Enable form elements and hide spinner
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
        }
        if (this.emailInput) {
          this.emailInput.disabled = false;
        }
        if (this.spinner) {
          this.spinner.classList.remove('show');
        }
      }
    }

    /**
     * Show error message to user
     * @param {string} message - The error message to display
     */
    showError(message) {
      if (this.errorText) {
        this.errorText.textContent = message;
      }
      if (this.errorMessage) {
        this.errorMessage.classList.add('show');
      }
    }

    /**
     * Hide error message
     */
    hideError() {
      if (this.errorMessage) {
        this.errorMessage.classList.remove('show');
      }
      if (this.errorText) {
        this.errorText.textContent = '';
      }
    }

    /**
     * Show success message
     */
    showSuccess() {
      if (this.successMessage) {
        this.successMessage.classList.add('show');
      }
    }

    /**
     * Hide success message
     */
    hideSuccess() {
      if (this.successMessage) {
        this.successMessage.classList.remove('show');
      }
    }

    /**
     * Get user-friendly error message from Firebase error
     * @param {Error} error - Firebase error object
     * @returns {string} User-friendly error message
     */
    getErrorMessage(error) {
      // Map Firebase error codes to user-friendly messages
      const errorMessages = {
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/user-not-found': 'No account found with this email address.',
        'auth/invalid-action-code': 'This sign-in link is invalid or has expired. Please request a new one.',
        'auth/expired-action-code': 'This sign-in link has expired. Please request a new one.',
        'auth/invalid-continue-uri': 'Invalid configuration. Please contact support.',
        'auth/missing-continue-uri': 'Invalid configuration. Please contact support.',
        'auth/unauthorized-continue-uri': 'Invalid configuration. Please contact support.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
        'auth/network-request-failed': 'Network error. Please check your connection and try again.',
      };

      // Use mapped Firebase message if we have a code
      if (error.code && errorMessages[error.code]) {
        return errorMessages[error.code];
      }
      // Use error.message for non-Firebase errors (e.g. API/network errors)
      if (error.message) {
        return error.message;
      }
      return 'An error occurred. Please try again.';
    }

    /**
     * Emit custom event for integration with other code
     * @param {string} eventName - Name of the event
     * @param {Object} detail - Event detail data
     */
    emitEvent(eventName, detail = {}) {
      // Create and dispatch custom event on the container element
      // Other code can listen with: element.addEventListener('firebase:eventName', handler)
      const event = new CustomEvent('firebase:' + eventName, {
        detail: detail,
        bubbles: true, // Allow event to bubble up the DOM
        cancelable: true
      });

      // Dispatch the native CustomEvent
      document.dispatchEvent(event);
    }

    /**
     * Get the current signed-in user
     * @returns {firebase.User|null}
     */
    getCurrentUser() {
      return this.auth.currentUser;
    }

    /**
     * Destroy the instance and clean up event listeners
     * Call this if you need to remove the component
     */
    destroy() {
      // Remove event listeners using native removeEventListener
      if (this.form) {
        this.form.removeEventListener('submit', this.handleFormSubmit);
      }
      if (this.signoutBtn) {
        this.signoutBtn.removeEventListener('click', this.handleSignOut);
      }
    }
  }

  // Export the class to global scope for use in other modules
  // This makes it available as window.FirebaseEmailLinkAuth
  // window.FirebaseEmailLinkAuth = FirebaseEmailLinkAuth;

  // Auto-initialize on DOMContentLoaded (equivalent to jQuery's $(document).ready())
  // This looks for any containers with the default ID and initializes them
  if (document.readyState === 'loading') {
    // DOM hasn't finished loading yet, wait for DOMContentLoaded
    document.addEventListener('DOMContentLoaded', initializeAuth);
  } else {
    // DOM is already loaded, initialize immediately
    initializeAuth();
  }

  /**
   * Initialize the Firebase Email Link Auth component
   * Checks for Firebase availability and auto-initializes the default container
   */
  function initializeAuth() {
    // Initialize Firebase app with configuration from WordPress
    // The site object is localized from WordPress via wp_localize_script
    // Use shared utility function to handle initialization safely
    const firebaseInitialized = initializeFirebase('firebase-email-link-auth.js');
    // // Only initialize if Firebase is loaded
    // if (typeof firebase === 'undefined') {
    //   console.warn('Firebase SDK not loaded. FirebaseEmailLinkAuth requires Firebase.');
    //   return;
    // }

    // // Check if Firebase Auth is available
    // if (!firebase.auth) {
    //   console.warn('Firebase Auth not loaded. FirebaseEmailLinkAuth requires Firebase Auth.');
    //   return;
    // }

    // // Check if Firebase app has been initialized
    // // Firebase must be initialized before calling firebase.auth()
    // if (!firebase.apps || firebase.apps.length === 0) {
    //   console.warn('Firebase app not initialized. FirebaseEmailLinkAuth requires Firebase to be initialized first.');
    //   // Retry after a short delay in case initialization is in progress
    //   setTimeout(initializeAuth, 100);
    //   return;
    // }

    try {
      // Guard against malformed or partially loaded Firebase SDK files.
      // This avoids throwing follow-up TypeErrors when `firebase-auth-compat.js`
      // fails to parse and `firebase.auth` is unavailable on the page.
      if (!firebaseInitialized || !hasFirebaseAuth()) {
        console.warn(
          'firebase-email-link-auth.js: Firebase Auth unavailable. Skipping auth module initialization.'
        );
        return;
      }

      const firebaseGlobal = getFirebaseGlobal();

      // Get the auth instance
      const auth = firebaseGlobal.auth();

      // Create and store the instance on the container element
      // Use dataset to store a reference (though we'll use a WeakMap for actual storage)
      window.firebaseEmailLinkAuth = new FirebaseEmailLinkAuth('firebase-email-link-auth', auth);

      console.log('firebaseEmailLinkAuth initialized successfully');
    } catch (error) {
      console.error('firebase-email-link-auth.js: Error initializing auth', error);
    }

    // Auto-initialize the default container if it exists
    const defaultContainer = document.getElementById('firebase-email-link-auth');
    if (defaultContainer) {
      try {
        // Store instance reference using a data attribute for compatibility
        // Note: This is just for reference, the instance is also stored in a WeakMap internally if needed
        defaultContainer.dataset.authInstanceInitialized = 'true';
      } catch (error) {
        console.error('FirebaseEmailLinkAuth: Error initializing auth', error);
      }
    }
  }

})();
